/**
 * Photos and video from a stop — `docs/activity.md` §11.3.
 *
 * They go through `useRecordUpload`, the record's one upload hook (F7), for
 * the stop's job: no describe dialog afterwards, the stop as their source,
 * the property as well when *Also show on the property's photos* is on, and
 * the upload queue of §11.4 as the uploader — which writes each one to the
 * phone first and attaches it itself, so a photo taken with no signal is
 * still attached when it goes up after the app was closed.
 *
 * A video longer than five minutes is refused before anything is stored,
 * with the sentence the server would print: *Videos can be up to 5 minutes.*
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { uuidv7 } from 'uuidv7'
import type { RecordRef } from '@rowted/shared'
import { useRecordUpload, type Uploader } from '../../record/use-record-upload.js'
import { uploadQueue } from './upload-queue.js'

export const VIDEO_MAX_SECONDS = 5 * 60
export const VIDEO_TOO_LONG = 'Videos can be up to 5 minutes.'
export const VIDEO_MAX_BYTES = 500 * 1024 * 1024
export const TOO_BIG_HERE = 'This one is too big to upload here.'

/** How long a video is, read from its own metadata; null where the phone cannot say. */
export function videoSeconds(file: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') { resolve(null); return }
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    const done = (value: number | null) => { URL.revokeObjectURL(url); resolve(value) }
    video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? Math.round(video.duration) : null)
    video.onerror = () => done(null)
    window.setTimeout(() => done(null), 8000)
    video.src = url
  })
}

export interface ScopeUpload {
  /** One stop's photos and videos — the grid of Board 15 B. */
  batch: string
  /** Photos (several at once) or a video, straight from the picker; how many were taken. */
  add: (files: FileList | File[], kind: 'photo' | 'video') => Promise<number>
  alsoShow: boolean
  setAlsoShow: (on: boolean) => void
  /** A refusal before upload — the video that was too long. */
  problem: string | null
  setProblem: (problem: string | null) => void
}

export function useScopeUpload({
  record, propertyId, orgUnitId, stopId,
}: {
  /** Where they are saved: the job, else the place, else the person. */
  record: RecordRef
  propertyId: string | null
  orgUnitId: string | null
  /** The stop they were taken at (`source_kind 'activity'`). */
  stopId: string
}): ScopeUpload {
  const batch = useRef(uuidv7())
  const [alsoShow, setAlsoShowState] = useState(true)
  const [problem, setProblem] = useState<string | null>(null)
  const kinds = useRef(new WeakMap<File, { kind: 'photo' | 'video'; seconds: number | null }>())

  const property = propertyId && !(record.type === 'property' && record.id === propertyId)
    ? { type: 'property' as const, id: propertyId } : null

  const uploader: Uploader = useCallback(async (file, context) => {
    const known = kinds.current.get(file)
    const item = await uploadQueue().addFile({
      batch: batch.current,
      blob: file,
      name: file.name || (known?.kind === 'video' ? 'Video.mp4' : 'Photo.jpg'),
      contentType: file.type || (known?.kind === 'video' ? 'video/mp4' : 'image/jpeg'),
      uploadKind: known?.kind ?? 'photo',
      type: known?.kind === 'video' ? 'video' : 'photo',
      ...(known?.seconds != null ? { durationSeconds: known.seconds } : {}),
      ...(context.orgUnitId ? { orgUnitId: context.orgUnitId } : {}),
      links: [context.record, ...context.alsoLink],
      ...(context.source ? { source: context.source } : {}),
    })
    return { id: item.clientId, linked: true }
  }, [])

  const source = useMemo(() => ({ kind: 'activity' as const, id: stopId }), [stopId])
  const alsoLink = useMemo(() => (alsoShow && property ? [property] : []), [alsoShow, property?.id])

  const upload = useRecordUpload({
    object: record.type,
    id: record.id,
    orgUnitId: orgUnitId ?? '',
    canEdit: true,
    describe: false,
    source,
    alsoLink,
    uploader,
  })

  const add = useCallback(async (files: FileList | File[], kind: 'photo' | 'video') => {
    setProblem(null)
    const chosen: File[] = []
    for (const file of Array.from(files)) {
      let seconds: number | null = null
      if (kind === 'video') {
        if (file.size > VIDEO_MAX_BYTES) { setProblem(TOO_BIG_HERE); continue }
        seconds = await videoSeconds(file)
        if (seconds !== null && seconds > VIDEO_MAX_SECONDS) { setProblem(VIDEO_TOO_LONG); continue }
      }
      kinds.current.set(file, { kind, seconds })
      chosen.push(file)
    }
    if (chosen.length > 0) await upload.add(chosen, { type: kind === 'video' ? 'video' : 'photo' })
    return chosen.length
  }, [upload])

  /* Switched after the photos were taken: the ones not yet attached follow it. */
  const setAlsoShow = useCallback((on: boolean) => {
    setAlsoShowState(on)
    const queue = uploadQueue()
    for (const item of queue.items()) {
      if (item.kind !== 'file' || item.batch !== batch.current) continue
      void queue.setLinks(item.clientId, on && property ? [record, property] : [record])
    }
  }, [record, property])

  return { batch: batch.current, add, alsoShow, setAlsoShow, problem: problem ?? upload.error, setProblem }
}
